import React, { useState } from 'react';
import { Hash, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const AddPageNumbers = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [position, setPosition] = useState('bottom-center');
    const [format, setFormat] = useState('number');
    const [startNumber, setStartNumber] = useState(1);
    const [fontSize, setFontSize] = useState(12);
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPageCount(pdf.getPageCount());
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const addPageNumbers = async () => {
        if (!file) return;

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

            setProgress(30);

            const pages = pdfDoc.getPages();

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const { width, height } = page.getSize();

                const pageNumber = i + startNumber;
                let text = '';

                switch (format) {
                    case 'number':
                        text = `${pageNumber}`;
                        break;
                    case 'page-of':
                        text = `Page ${pageNumber} of ${pages.length}`;
                        break;
                    case 'slash':
                        text = `${pageNumber}/${pages.length}`;
                        break;
                }

                const textWidth = font.widthOfTextAtSize(text, fontSize);
                let x, y;

                switch (position) {
                    case 'top-left':
                        x = 50;
                        y = height - 50;
                        break;
                    case 'top-center':
                        x = (width - textWidth) / 2;
                        y = height - 50;
                        break;
                    case 'top-right':
                        x = width - textWidth - 50;
                        y = height - 50;
                        break;
                    case 'bottom-left':
                        x = 50;
                        y = 30;
                        break;
                    case 'bottom-center':
                        x = (width - textWidth) / 2;
                        y = 30;
                        break;
                    case 'bottom-right':
                        x = width - textWidth - 50;
                        y = 30;
                        break;
                }

                page.drawText(text, {
                    x,
                    y,
                    size: fontSize,
                    font,
                    color: rgb(0, 0, 0),
                });

                setProgress(30 + ((i + 1) / pages.length) * 60);
            }

            setProgress(95);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            const filename = `${fileName}_numbered.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('Page numbers added! Opening preview...');

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to add page numbers: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <Hash size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Add Page Numbers
                </h1>
                <p className="tool-description">
                    Add page numbers to your PDF with customizable position, format, and styling.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPageCount(0); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                Page Number Settings
                            </h3>

                            <div className="grid-2" style={{ marginBottom: 'var(--spacing-4)' }}>
                                <div>
                                    <label className="label">Position</label>
                                    <select value={position} onChange={(e) => setPosition(e.target.value)} className="input">
                                        <option value="top-left">Top Left</option>
                                        <option value="top-center">Top Center</option>
                                        <option value="top-right">Top Right</option>
                                        <option value="bottom-left">Bottom Left</option>
                                        <option value="bottom-center">Bottom Center</option>
                                        <option value="bottom-right">Bottom Right</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="label">Format</label>
                                    <select value={format} onChange={(e) => setFormat(e.target.value)} className="input">
                                        <option value="number">1, 2, 3...</option>
                                        <option value="page-of">Page 1 of 10</option>
                                        <option value="slash">1/10</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="label">Starting Number</label>
                                    <input
                                        type="number"
                                        value={startNumber}
                                        onChange={(e) => setStartNumber(parseInt(e.target.value) || 1)}
                                        min="1"
                                        className="input"
                                    />
                                </div>

                                <div>
                                    <label className="label">Font Size</label>
                                    <input
                                        type="number"
                                        value={fontSize}
                                        onChange={(e) => setFontSize(parseInt(e.target.value) || 12)}
                                        min="8"
                                        max="24"
                                        className="input"
                                    />
                                </div>
                            </div>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button variant="primary" size="lg" onClick={addPageNumbers} disabled={isProcessing} loading={isProcessing} fullWidth>
                            <Download size={20} />
                            Add Page Numbers
                        </Button>
                    </div>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default AddPageNumbers;
